import React, { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Bed as BedIcon,
  Person as PersonIcon,
  TransferWithinAStation as TransferIcon,
  ExitToApp as DischargeIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  FilterList as FilterIcon,
  TableChart as TableChartIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';


import {
  getBedAssignmentsByFacility,
  getRoomAssignmentsByFacility,
  getActiveEncountersByFacility,
  getPatientById,
  getCurrentBedAssignment,
  getCurrentRoomAssignment,
  getAvailableBeds,
  getAvailableStandaloneRooms,
  dischargePatientFromBed,
  dischargePatientFromRoom
} from '../../services/bedAssignmentService';
import { BedStatus } from '../../types/enums';
import BedAssignmentManager from '../bed-assignment/BedAssignmentManager';
import BedAssignmentGrid from './BedAssignmentGrid';
import { useUserData } from '../../hooks/useUserData';

const BedAssignmentPage = ({ 
  facilityId: propFacilityId, 
  patientId: propPatientId, 
  encounterId: propEncounterId,
  patient: propPatient,
  encounter: propEncounter,
  showHeader = true,
  compact = false
}) => {

  const { user, defaultFacility } = useUserData();
  


  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // New state for active encounters
  const [activeEncounters, setActiveEncounters] = useState([]);
  const [encounterFilter, setEncounterFilter] = useState('no-room-beds'); // 'no-room-beds' or 'all-active'
  const [encounterSearchTerm, setEncounterSearchTerm] = useState('');
  const [loadingEncounters, setLoadingEncounters] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  
  // Modal states
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [showDischargeDialog, setShowDischargeDialog] = useState(false);
  
  // New modal state for bed assignment
  const [selectedPatientForAssignment, setSelectedPatientForAssignment] = useState(null);
  const [showBedAssignmentDialog, setShowBedAssignmentDialog] = useState(false);

  // Use prop facility or user's default facility
  const effectiveFacilityId = defaultFacility?.id || '';
  
  // Show error if default facility is missing
  useEffect(() => {
    if (!defaultFacility?.id) {
      setError('Default facility is not set. Please set your default facility to manage bed assignments.');
    } else {
      // Clear the error if it was about missing default facility
      setError((prevError) => 
        prevError === 'Default facility is not set. Please set your default facility to manage bed assignments.' 
          ? '' 
          : prevError
      );
    }
  }, [defaultFacility?.id]);
  const isEmbedded = !!propFacilityId;



  const loadAssignmentData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Load active bed assignments for the facility
      const bedAssignments = await getBedAssignmentsByFacility(effectiveFacilityId);
      const roomAssignments = await getRoomAssignmentsByFacility(effectiveFacilityId);

      const allAssignments = [...bedAssignments, ...roomAssignments].sort((a, b) => {
        const aTime = a.assignedAt?.toDate?.() || new Date(a.assignedAt);
        const bTime = b.assignedAt?.toDate?.() || new Date(b.assignedAt);
        return bTime - aTime;
      });



      setActiveAssignments(allAssignments);

      // Load available beds and rooms for stats
      const [bedsResponse, roomsResponse] = await Promise.all([
        getAvailableBeds(effectiveFacilityId),
        getAvailableStandaloneRooms(effectiveFacilityId)
      ]);

      // Extract the actual arrays from the response objects
      const beds = bedsResponse?.availableBeds || [];
      const rooms = roomsResponse?.availableRooms || [];
      
      // Debug logging to help identify data structure issues
      console.log('Available beds response:', bedsResponse);
      console.log('Available rooms response:', roomsResponse);
      console.log('Processed beds array:', beds);
      console.log('Processed rooms array:', rooms);
      console.log('Sample bed structure:', beds[0]);
      console.log('Sample room structure:', rooms[0]);
      
      setAvailableBeds(beds);
      setAvailableRooms(rooms);

    } catch (err) {
      setError('Failed to load assignment data: ' + err.message);
      console.error('Error loading assignment data:', err);
    } finally {
      setLoading(false);
    }
  }, [effectiveFacilityId]);

  const loadActiveEncounters = useCallback(async () => {
    setLoadingEncounters(true);
    try {
      // Load active encounters for the facility
      const encounters = await getActiveEncountersByFacility(effectiveFacilityId);

      // Fetch patient data for each encounter
      const encountersWithPatientData = await Promise.all(
        encounters.map(async (encounter) => {
          let patientData = null;
          
          // Try to fetch patient data from patients collection
          if (encounter.patientId) {
            try {
              patientData = await getPatientById(encounter.patientId);
            } catch (error) {
              console.warn(`Could not fetch patient data for ${encounter.patientId}:`, error);
              // Use encounter data as fallback if patient lookup fails
              patientData = {
                id: encounter.patientId,
                name: encounter.patientName || 'Unknown Patient',
                dateOfBirth: encounter.patientDateOfBirth,
                gender: encounter.patientGender
              };
            }
          }

          // For each encounter, check if patient has room/bed assignment
          const [bedAssignment, roomAssignment] = await Promise.all([
            getCurrentBedAssignment(encounter.patientId),
            getCurrentRoomAssignment(encounter.patientId)
          ]);

          return {
            ...encounter,
            patientData, // Store the full patient data
            bedAssignment,
            roomAssignment
          };
        })
      );

      setActiveEncounters(encountersWithPatientData);
      
      // Debug logging to help identify data structure issues
      if (encountersWithPatientData.length > 0) {
        console.log('Sample encounter with patient data:', encountersWithPatientData[0]);
        console.log('Patient data structure:', encountersWithPatientData[0].patientData);
      }
    } catch (err) {
      console.error('Error loading active encounters:', err);
      setError('Failed to load active encounters: ' + err.message);
    } finally {
      setLoadingEncounters(false);
    }
  }, [effectiveFacilityId]);

  // Load bed assignment data when facility changes
  useEffect(() => {
    if (effectiveFacilityId) {
      loadAssignmentData();
      loadActiveEncounters();
    }
  }, [effectiveFacilityId, loadAssignmentData, loadActiveEncounters]);

  const handleDischarge = async (assignment) => {
    try {
      if (assignment.type === 'bed') {
        await dischargePatientFromBed(assignment.id, user.uid, user.role || 'nurse', 'Discharged via dashboard');
      } else {
        await dischargePatientFromRoom(assignment.id, user.uid, user.role || 'nurse', 'Discharged via dashboard');
      }
      setSuccess(`Patient ${assignment.patientName} successfully discharged`);
      setShowDischargeDialog(false);
      setSelectedAssignment(null);
      loadAssignmentData();
      loadActiveEncounters(); // Refresh encounters after discharge
    } catch (err) {
      setError('Discharge failed: ' + err.message);
    }
  };

  const handleAssignBed = (encounter) => {
    // Get patient data from the fetched patient document
    const patientData = encounter.patientData;
    
    const patientName = patientData?.name || 
                      patientData?.fullName ||
                      (patientData?.firstName && patientData?.lastName ? 
                        `${patientData.firstName} ${patientData.lastName}` : '') ||
                      encounter.patientName || // fallback to encounter data
                      'Unknown Patient';
    
    const patientMRN = patientData?.mrn || 
                     patientData?.MRN ||
                     encounter.patientMRN || // fallback to encounter data
                     'N/A';

    setSelectedPatientForAssignment({
      patient: {
        id: encounter.patientId,
        name: patientName,
        mrn: patientMRN
      },
      encounter: {
        id: encounter.id,
        type: encounter.type || encounter.encounterType,
        department: encounter.department || encounter.departmentName
      }
    });
    setShowBedAssignmentDialog(true);
  };

  const handleBedAssignmentComplete = () => {
    setShowBedAssignmentDialog(false);
    setSelectedPatientForAssignment(null);
    loadAssignmentData();
    loadActiveEncounters();
    setSuccess('Bed assignment completed successfully');
  };

  // Filter assignments based on search term
  const filteredAssignments = (activeAssignments || []).filter(assignment =>
    (assignment && assignment.patientName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (assignment && assignment.patientMRN?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (assignment && assignment.bedId?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (assignment && assignment.roomId?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (assignment && assignment.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filter encounters based on filter type and search term
  const filteredEncounters = activeEncounters.filter(encounter => {
    // Get patient data from the fetched patient document
    const patientData = encounter.patientData;
    
    const patientName = patientData?.name || 
                      patientData?.fullName ||
                      (patientData?.firstName && patientData?.lastName ? 
                        `${patientData.firstName} ${patientData.lastName}` : '') ||
                      encounter.patientName || // fallback to encounter data
                      'Unknown Patient';
    
    const patientMRN = patientData?.mrn || 
                     patientData?.MRN ||
                     encounter.patientMRN || // fallback to encounter data
                     'N/A';

    // Search filter
    const matchesSearch = !encounterSearchTerm || 
      patientName.toLowerCase().includes(encounterSearchTerm.toLowerCase()) ||
      patientMRN.toLowerCase().includes(encounterSearchTerm.toLowerCase()) ||
      (encounter.department || encounter.departmentName || '').toLowerCase().includes(encounterSearchTerm.toLowerCase());

    // Room/Bed filter
    if (encounterFilter === 'no-room-beds') {
      return matchesSearch && !encounter.bedAssignment && !encounter.roomAssignment;
    } else {
      return matchesSearch; // 'all-active'
    }
  });

  // If this is an embedded component with specific patient/encounter, show the manager directly
  if (isEmbedded && propPatientId && propEncounterId) {
    return (
      <BedAssignmentManager
        facilityId={effectiveFacilityId}
        patientId={propPatientId}
        encounterId={propEncounterId}
        patient={propPatient}
        encounter={propEncounter}
      />
    );
  }

  if (!effectiveFacilityId && !loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const occupiedBeds = (availableBeds || []).filter(bed => bed.status === BedStatus.OCCUPIED).length;
  const totalBeds = (availableBeds || []).length;
  const occupiedRooms = (availableRooms || []).filter(room => room.status === 'occupied').length;
  const totalRooms = (availableRooms || []).length;

  return (
    <Box sx={{ p: 1 }}>
      {/* Compact Header */}
      {showHeader && (
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={1.5}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '10px 16px',
            borderRadius: '6px',
            boxShadow: '0 3px 12px rgba(118, 75, 162, 0.2)',
          }}
        >
          <Typography 
            variant="h6" 
            component="h1" 
            sx={{ 
              color: 'white', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              fontSize: '1.1rem'
            }}
          >
            <BedIcon sx={{ mr: 1, fontSize: '1.1em' }} />
            Bed Assignment Dashboard
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadAssignmentData();
              loadActiveEncounters();
            }}
            disabled={loading || loadingEncounters}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
              fontSize: '0.75rem',
              py: 0.5
            }}
          >
            Refresh
          </Button>
        </Box>
      )}

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Compact Summary Cards */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ minHeight: 'unset' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" variant="body2" gutterBottom sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                Occupied Beds
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                {occupiedBeds}/{totalBeds}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                {totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0}% occupancy
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ minHeight: 'unset' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" variant="body2" gutterBottom sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                Occupied Rooms
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                {occupiedRooms}/{totalRooms}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                {totalRooms > 0 ? Math.round((occupiedRooms / totalRooms) * 100) : 0}% occupancy
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ minHeight: 'unset' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" variant="body2" gutterBottom sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                Active Assignments
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                {activeAssignments.length}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                {activeAssignments.filter(a => a.type === 'bed').length} beds, {activeAssignments.filter(a => a.type === 'room').length} rooms
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ minHeight: 'unset' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" variant="body2" gutterBottom sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                Available Capacity
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                {(totalBeds - occupiedBeds) + (totalRooms - occupiedRooms)}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                {totalBeds - occupiedBeds} beds, {totalRooms - occupiedRooms} rooms
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* View Toggle and Search Bar */}
      <Box display="flex" alignItems="center" gap={2} mb={1.5}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newView) => newView && setViewMode(newView)}
          size="small"
          sx={{ height: '40px' }}
        >
          <ToggleButton value="table" sx={{ px: 1.5, py: 0.25 }}>
            <TableChartIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
            Table View
          </ToggleButton>
          <ToggleButton value="grid" sx={{ px: 1.5, py: 0.25 }}>
            <ViewModuleIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
            Grid View
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Search by patient name, MRN, bed/room ID, or department..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
          {(filteredAssignments || []).length} assignments
        </Typography>
      </Box>

      {/* Assignments View (Table or Grid) */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
              <CircularProgress size={24} />
            </Box>
          ) : viewMode === 'grid' ? (
            <BedAssignmentGrid
              departments={[]}
              availableBeds={availableBeds || []}
              availableRooms={availableRooms || []}
              activeBedAssignments={(filteredAssignments || []).filter(a => a && a.type === 'bed') || []}
              activeRoomAssignments={(filteredAssignments || []).filter(a => a && a.type === 'room') || []}
              onRefresh={() => {
                loadAssignmentData();
                loadActiveEncounters();
              }}
              onAssign={handleAssignBed}
              onDischarge={(assignment) => {
                setSelectedAssignment(assignment);
                setShowDischargeDialog(true);
              }}
              onView={(assignment) => {
                setSelectedAssignment(assignment);
                setShowAssignmentDialog(true);
              }}
              searchTerm={searchTerm}
              loading={loading}
              selectedDepartment="all"
            />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Patient</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>MRN</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Assigned</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(filteredAssignments || []).map((assignment) => {
                    if (!assignment) return null;
                    
                    const assignedDate = assignment.assignedAt?.toDate?.() || new Date(assignment.assignedAt);
                    const duration = Math.floor((new Date() - assignedDate) / 1000 / 60 / 60); // hours
                    
                    return (
                      <TableRow key={assignment.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: '0.9rem' }} />
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                                {assignment.patientName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {assignment.encounterType}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{assignment.patientMRN}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={assignment.type.toUpperCase()} 
                            size="small" 
                            color={assignment.type === 'bed' ? 'primary' : 'secondary'}
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {assignment.type === 'bed' ? assignment.bedId : assignment.roomId}
                          </Typography>
                          {assignment.type === 'bed' && assignment.roomId && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              Room: {assignment.roomId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{assignment.departmentName || assignment.department || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {assignedDate.toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {assignedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {duration < 24 ? `${duration}h` : `${Math.floor(duration / 24)}d`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.25}>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small"
                                sx={{ p: 0.5 }}
                                onClick={() => {
                                  setSelectedAssignment(assignment);
                                  setShowAssignmentDialog(true);
                                }}
                              >
                                <ViewIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Transfer">
                              <IconButton 
                                size="small" 
                                sx={{ p: 0.5 }}
                                onClick={() => {
                                  setSelectedAssignment(assignment);
                                  setShowAssignmentDialog(true);
                                }}
                              >
                                <TransferIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Discharge">
                              <IconButton 
                                size="small"
                                sx={{ p: 0.5 }}
                                onClick={() => {
                                  setSelectedAssignment(assignment);
                                  setShowDischargeDialog(true);
                                }}
                              >
                                <DischargeIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(filteredAssignments || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {searchTerm ? 'No assignments match your search' : 'No active assignments found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Active Patients Panel */}
      <Card>
        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
          {/* Header with filters */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
              <PersonIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
              Active Patients ({(filteredEncounters || []).length})
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <ToggleButtonGroup
                value={encounterFilter}
                exclusive
                onChange={(e, newFilter) => newFilter && setEncounterFilter(newFilter)}
                size="small"
              >
                <ToggleButton value="no-room-beds" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                  <FilterIcon sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                  With No Room/Beds
                </ToggleButton>
                <ToggleButton value="all-active" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                  All Active
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Search bar for encounters */}
          <TextField
            size="small"
            placeholder="Search patients by name, MRN, or department..."
            value={encounterSearchTerm}
            onChange={(e) => setEncounterSearchTerm(e.target.value)}
            sx={{ mb: 1.5, width: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Patients table */}
          {loadingEncounters ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Patient Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Date of Birth</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Admission Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Room No.</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Bed No.</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(filteredEncounters || []).map((encounter) => {
                    if (!encounter) return null;
                    
                    const admissionDate = encounter.admissionDate?.toDate?.() || new Date(encounter.admissionDate);
                    
                    // Get patient data from the fetched patient document
                    const patientData = encounter.patientData;
                    
                    // Handle date of birth from patient data
                    let dateOfBirth = null;
                    if (patientData?.dateOfBirth) {
                      dateOfBirth = patientData.dateOfBirth?.toDate?.() || new Date(patientData.dateOfBirth);
                    }
                    
                    // Get patient name and MRN from patient data
                    const patientName = patientData?.name || 
                                      patientData?.fullName ||
                                      (patientData?.firstName && patientData?.lastName ? 
                                        `${patientData.firstName} ${patientData.lastName}` : '') ||
                                      encounter.patientName || // fallback to encounter data
                                      'Unknown Patient';
                    
                    const patientMRN = patientData?.mrn || 
                                     patientData?.MRN ||
                                     encounter.patientMRN || // fallback to encounter data
                                     'N/A';
                    
                    return (
                      <TableRow 
                        key={encounter.id} 
                        hover 
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => handleAssignBed(encounter)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: '0.9rem' }} />
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                                {patientName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                MRN: {patientMRN}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {dateOfBirth && !isNaN(dateOfBirth) ? dateOfBirth.toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {admissionDate.toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {admissionDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {encounter.roomAssignment?.roomId || encounter.bedAssignment?.roomId || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {encounter.bedAssignment?.bedId || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {encounter.department || encounter.departmentName || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.25}>
                            <Tooltip title="View Encounter">
                              <IconButton 
                                size="small"
                                sx={{ p: 0.5 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Add view encounter logic here
                                }}
                              >
                                <ViewIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Assign Bed">
                              <IconButton 
                                size="small"
                                sx={{ p: 0.5 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignBed(encounter);
                                }}
                              >
                                <AssignmentIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(filteredEncounters || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {encounterSearchTerm ? 'No patients match your search' : 
                           encounterFilter === 'no-room-beds' ? 'No patients without room/bed assignments' : 
                           'No active patients found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Assignment Details Dialog */}
      <Dialog 
        open={showAssignmentDialog} 
        onClose={() => setShowAssignmentDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Assignment Details: {selectedAssignment?.patientName}
        </DialogTitle>
        <DialogContent>
          {selectedAssignment && (
            <BedAssignmentManager
              facilityId={effectiveFacilityId}
              patientId={selectedAssignment.patientId}
              encounterId={selectedAssignment.encounterId}
              patient={{ 
                id: selectedAssignment.patientId, 
                name: selectedAssignment.patientName, 
                mrn: selectedAssignment.patientMRN 
              }}
              encounter={{ 
                id: selectedAssignment.encounterId, 
                type: selectedAssignment.encounterType,
                department: selectedAssignment.departmentName
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssignmentDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Bed Assignment Dialog */}
      <Dialog 
        open={showBedAssignmentDialog} 
        onClose={() => setShowBedAssignmentDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Bed Assignment: {selectedPatientForAssignment?.patient?.name}
        </DialogTitle>
        <DialogContent>
          {selectedPatientForAssignment && (
            <BedAssignmentManager
              facilityId={effectiveFacilityId}
              patientId={selectedPatientForAssignment.patient.id}
              encounterId={selectedPatientForAssignment.encounter.id}
              patient={selectedPatientForAssignment.patient}
              encounter={selectedPatientForAssignment.encounter}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBedAssignmentDialog(false)}>Cancel</Button>
          <Button onClick={handleBedAssignmentComplete} variant="contained">
            Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discharge Confirmation Dialog */}
      <Dialog open={showDischargeDialog} onClose={() => setShowDischargeDialog(false)}>
        <DialogTitle>Confirm Discharge</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to discharge {selectedAssignment?.patientName} from {' '}
            {selectedAssignment?.type === 'bed' ? `bed ${selectedAssignment?.bedId}` : `room ${selectedAssignment?.roomId}`}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDischargeDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => handleDischarge(selectedAssignment)} 
            color="primary" 
            variant="contained"
          >
            Discharge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BedAssignmentPage; 